/**
 * Bespoke three-dot "thinking" loader shown while the agent is generating a
 * response. Presents animated flashing dots in place of a circular spinner so
 * the waiting state reads like a chat message being composed.
 *
 * Implemented with a scoped <style> keyframes block + inline styles (matching the
 * pattern used in ToolIndicator) rather than @emotion/css, which is only a
 * transitive dependency in this package and not declared in package.json.
 */
import React from 'react';

type DotSize = 'small' | 'medium';

interface DotLoaderProps {
  size?: DotSize;
  accessibleLabel?: string;
}

const DOT_SIZES: Record<DotSize, number> = {
  small: 6,
  medium: 8,
};

// Unique animation name per instance-agnostic keyframes; staggered delays create
// the left-to-right flashing wave.
const KEYFRAMES = `
@keyframes chat-dot-flash {
  0%, 80%, 100% { opacity: 0.2; }
  40% { opacity: 1; }
}`;

const visuallyHidden: React.CSSProperties = {
  clip: 'rect(0 0 0 0)',
  overflow: 'hidden',
  position: 'absolute',
  top: 0,
  left: 0,
  border: 0,
  width: 1,
  height: 1,
  opacity: 0,
};

export const DotLoader: React.FC<DotLoaderProps> = ({ size = 'medium', accessibleLabel = 'Loading' }) => {
  const dotSize = DOT_SIZES[size];
  const gap = dotSize / 2;

  const dotStyle = (delaySeconds: number): React.CSSProperties => ({
    width: `${dotSize}px`,
    height: `${dotSize}px`,
    display: 'inline-block',
    backgroundColor: 'currentColor',
    borderRadius: '100%',
    animation: 'chat-dot-flash 1.4s ease-in-out infinite both',
    animationDelay: `${delaySeconds}s`,
  });

  return (
    <span aria-label={accessibleLabel} data-testid="chat-dot-loader" role="status" style={{ display: 'inline-flex', alignItems: 'center', verticalAlign: 'middle', minHeight: '1.4em', gap: `${gap}px`, color: '#8c8c8c' }}>
      <style>{KEYFRAMES}</style>
      <span style={dotStyle(0)} />
      <span style={dotStyle(0.2)} />
      <span style={dotStyle(0.4)} />
      <span style={visuallyHidden}>{accessibleLabel}</span>
    </span>
  );
};
